import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Input } from "antd";
import Swal from "sweetalert2";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  Video,
  Play,
  Share,
  Edit2,
  Trash,
  Folder,
  SearchNormal1,
  CloseCircle,
  Camera,
} from "iconsax-react";

import { favoritesService, UserVideo } from "../../services/favoritesService";
import { useAuth } from "../../hooks/auth";
import { useGamification } from "../../hooks/useGamification";
import MobileLayout from "../../components/MobileLayout";

import {
  PageWrap,
  FavoritesHint,
  HeroCard,
  HeroTop,
  HeroBadge,
  HeroFolder,
  HeroTitle,
  HeroSub,
  ProgressTrack,
  ProgressFill,
  HeroFoot,
  SearchCard,
  SearchInput,
  VideoCard,
  VideoRow,
  VideoIconBox,
  VideoText,
  VideoTitle,
  VideoDesc,
  PlayBtn,
  Divider,
  ShareRow,
  ShareIconWrap,
  ShareLabel,
  ActionsRow,
  EditAction,
  DeleteAction,
  ActionIconSm,
  EmptyWrap,
  EmptyCircle,
  EmptyTitle,
  EmptyText,
  FabWrap,
  FabBtn,
  LoadingCenter,
  PRIMARY,
  DANGER,
} from "./styles";

const PLAY_STORE_SINALARIO =
  "https://play.google.com/store/apps/details?id=com.bilingua.sinalario";
const MAX_VIDEOS = 50;

const PersonalRepository: React.FC = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<UserVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [shareCounts, setShareCounts] = useState<Record<string, number>>({});
  const [playVideo, setPlayVideo] = useState<UserVideo | null>(null);
  const [editVideo, setEditVideo] = useState<UserVideo | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { user } = useAuth();
  const { addPoints } = useGamification();
  const userId = user?.uid;

  const loadData = useCallback(async () => {
    if (!userId) {
      setVideos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await favoritesService.getUserVideos(userId);
      setVideos(list);
    } catch (e) {
      console.error(e);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const q = query.trim().toLowerCase();

  const filteredVideos = useMemo(() => {
    if (!q) return videos;
    return videos.filter(
      (v) =>
        (v.title || "").toLowerCase().includes(q) ||
        (v.description || "").toLowerCase().includes(q)
    );
  }, [videos, q]);

  const openEdit = (v: UserVideo) => {
    setEditVideo(v);
    setEditTitle(v.title);
    setEditDescription(v.description || "");
  };

  const saveEdit = async () => {
    if (!userId || !editVideo) return;
    const t = editTitle.trim();
    if (!t) {
      Swal.fire({ icon: "warning", title: "Título obrigatório" });
      return;
    }
    setSavingEdit(true);
    const ok = await favoritesService.updateVideo(userId, editVideo.id, {
      title: t,
      description: editDescription.trim(),
    });
    setSavingEdit(false);
    if (ok) {
      setEditVideo(null);
      loadData();
    } else {
      Swal.fire({ icon: "error", title: "Não foi possível salvar" });
    }
  };

  const handleDelete = (v: UserVideo) => {
    Swal.fire({
      title: "Excluir vídeo?",
      text: v.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: DANGER,
      cancelButtonText: "Cancelar",
      confirmButtonText: "Excluir",
    }).then(async (r) => {
      if (r.isConfirmed && userId) {
        const ok = await favoritesService.deleteVideo(userId, v.id);
        if (ok) loadData();
      }
    });
  };

  const handleShare = async (v: UserVideo) => {
    const n = shareCounts[v.id] || 0;
    if (n >= 5) {
      Swal.fire({
        title: "Limite de compartilhamento",
        text: "Você só pode compartilhar este vídeo até 5 vezes.",
        icon: "info",
      });
      return;
    }
    const text =
      `Confira este vídeo do meu sinal no Sinalário de Libras!\n\n` +
      `Palavra: ${v.title}\n` +
      `Descrição: ${v.description || "—"}\n\n` +
      `Baixe o app na Google Play: ${PLAY_STORE_SINALARIO}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: v.title, text, url: v.videoUrl });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text}\n\n${v.videoUrl}`);
        Swal.fire({ icon: "success", title: "Copiado", timer: 1500 });
      } else {
        window.open(v.videoUrl, "_blank", "noopener,noreferrer");
      }
      setShareCounts((prev) => ({ ...prev, [v.id]: n + 1 }));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "";
      if (!msg.includes("abort")) {
        Swal.fire({
          icon: "error",
          title: "Erro ao compartilhar",
          text: "Tente novamente.",
        });
      }
    }
  };

  const pickUpload = () => {
    if (videos.length >= MAX_VIDEOS) {
      Swal.fire({
        title: "Limite atingido",
        text: "Você já atingiu o limite máximo de 50 vídeos.",
        icon: "info",
      });
      return;
    }
    fileRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !userId) return;

    if (videos.length >= MAX_VIDEOS) {
      Swal.fire({
        title: "Limite atingido",
        text: "Limite de 50 vídeos.",
        icon: "info",
      });
      return;
    }

    setUploading(true);
    try {
      Swal.fire({
        title: "Enviando vídeo...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const storage = getStorage();
      const safeName = file.name.replace(/[^\w.-]+/g, "_");
      const path = `users/${userId}/videos/${Date.now()}_${safeName}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      const baseTitle = file.name.replace(/\.[^.]+$/, "") || "Meu vídeo";
      const newId = await favoritesService.addVideo(userId, {
        title: baseTitle,
        description: "",
        videoUrl: url,
      });
      Swal.close();
      if (newId) {
        await addPoints("UPLOAD_VIDEO");
        await loadData();
        Swal.fire({ icon: "success", title: "Vídeo enviado!", timer: 1800 });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: "Não foi possível salvar o vídeo.",
        });
      }
    } catch (err) {
      console.error(err);
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Falha no envio",
        html: `Na web, o envio depende das regras do Storage. Você também pode usar o <a href="${PLAY_STORE_SINALARIO}" target="_blank" rel="noreferrer">aplicativo</a>.`,
      });
    } finally {
      setUploading(false);
    }
  };

  const remaining = MAX_VIDEOS - videos.length;
  const pct = Math.min(100, (videos.length / MAX_VIDEOS) * 100);

  return (
    <MobileLayout headerTitle="Sinalário Pessoal" headerType="private">
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        hidden
        onChange={onFileChange}
      />

      <PageWrap>
        <FavoritesHint type="button" onClick={() => navigate("/favorites")}>
          Sinais favoritos — abra a aba Favoritos na barra inferior
        </FavoritesHint>

        <HeroCard>
          <HeroTop>
            <HeroBadge>Espaço usado</HeroBadge>
            <HeroFolder>
              <Folder size={22} color="#fff" variant="Bold" />
            </HeroFolder>
          </HeroTop>
          <HeroTitle>{`${videos.length} de ${MAX_VIDEOS} vídeos`}</HeroTitle>
          <HeroSub>
            {videos.length === MAX_VIDEOS
              ? "Você atingiu o limite de vídeos!"
              : `Ainda é possível enviar ${remaining} vídeo(s)`}
          </HeroSub>
          <ProgressTrack>
            <ProgressFill $pct={pct} />
          </ProgressTrack>
          <HeroFoot>
            <span>{videos.length} vídeos</span>
            <span>{remaining} restantes</span>
          </HeroFoot>
        </HeroCard>

        <SearchCard>
          <SearchNormal1 size={20} color="#64748b" variant="Bold" />
          <SearchInput
            type="search"
            placeholder="Pesquisar meus vídeos..."
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
            aria-label="Pesquisar vídeos"
          />
          {query ? (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => setQuery("")}
              style={{
                border: "none",
                background: "none",
                padding: 4,
                cursor: "pointer",
              }}
            >
              <CloseCircle size={24} color="#64748b" variant="Bold" />
            </button>
          ) : null}
        </SearchCard>

        {loading && <LoadingCenter>Carregando seus vídeos...</LoadingCenter>}

        {!loading && videos.length === 0 && (
          <EmptyWrap>
            <EmptyCircle>
              <Video size={40} color={PRIMARY} variant="Outline" />
            </EmptyCircle>
            <EmptyTitle>Você ainda não enviou nenhum vídeo.</EmptyTitle>
            <EmptyText>
              Comece compartilhando seus sinais em Libras! Use o botão Enviar
              vídeo ou o aplicativo.
            </EmptyText>
          </EmptyWrap>
        )}

        {!loading &&
          videos.length > 0 &&
          filteredVideos.length === 0 &&
          q.length > 0 && (
            <EmptyWrap>
              <EmptyCircle>
                <SearchNormal1 size={40} color={PRIMARY} variant="Outline" />
              </EmptyCircle>
              <EmptyTitle>{`Nenhum vídeo encontrado para "${query.trim()}".`}</EmptyTitle>
              <EmptyText>Tente pesquisar por outro termo.</EmptyText>
            </EmptyWrap>
          )}

        {!loading &&
          filteredVideos.map((v) => (
            <VideoCard key={v.id}>
              <VideoRow>
                <VideoIconBox>
                  <Video size={24} color="#fff" variant="Bold" />
                </VideoIconBox>
                <VideoText>
                  <VideoTitle>{v.title}</VideoTitle>
                  <VideoDesc>
                    {v.description?.trim() ? v.description : "Sem descrição"}
                  </VideoDesc>
                </VideoText>
                <PlayBtn
                  type="button"
                  aria-label="Assistir"
                  onClick={() => setPlayVideo(v)}
                >
                  <Play size={20} color={PRIMARY} variant="Bold" />
                </PlayBtn>
              </VideoRow>

              <Divider />

              <ShareRow
                type="button"
                disabled={(shareCounts[v.id] || 0) >= 5}
                onClick={() => handleShare(v)}
              >
                <ShareIconWrap>
                  <Share size={18} color="#fff" variant="Bold" />
                </ShareIconWrap>
                <ShareLabel>Compartilhar</ShareLabel>
              </ShareRow>

              <ActionsRow>
                <EditAction type="button" onClick={() => openEdit(v)}>
                  <ActionIconSm $bg={PRIMARY}>
                    <Edit2 size={18} color="#fff" variant="Bold" />
                  </ActionIconSm>
                  Editar
                </EditAction>
                <DeleteAction type="button" onClick={() => handleDelete(v)}>
                  <ActionIconSm $bg={DANGER}>
                    <Trash size={18} color="#fff" variant="Bold" />
                  </ActionIconSm>
                  Excluir
                </DeleteAction>
              </ActionsRow>
            </VideoCard>
          ))}
      </PageWrap>

      <FabWrap>
        <FabBtn
          type="button"
          disabled={uploading || videos.length >= MAX_VIDEOS}
          onClick={pickUpload}
        >
          <Camera size={24} color="#fff" variant="Bold" />
          {uploading ? "Enviando..." : "Enviar Vídeo"}
        </FabBtn>
      </FabWrap>

      <Modal
        open={!!playVideo}
        onCancel={() => setPlayVideo(null)}
        footer={null}
        title={playVideo?.title}
        centered
        width="min(100%, 420px)"
        destroyOnClose
      >
        {playVideo?.videoUrl ? (
          <video
            src={playVideo.videoUrl}
            controls
            playsInline
            style={{ width: "100%", borderRadius: 12 }}
          >
            <track kind="captions" />
          </video>
        ) : null}
      </Modal>

      <Modal
        open={!!editVideo}
        onCancel={() => setEditVideo(null)}
        onOk={saveEdit}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={savingEdit}
        title="Editar vídeo"
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span id="repo-edit-title-lbl">Título</span>
            <Input
              aria-labelledby="repo-edit-title-lbl"
              value={editTitle}
              onChange={(ev) => setEditTitle(ev.target.value)}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span id="repo-edit-desc-lbl">Descrição</span>
            <Input.TextArea
              aria-labelledby="repo-edit-desc-lbl"
              rows={3}
              value={editDescription}
              onChange={(ev) => setEditDescription(ev.target.value)}
            />
          </div>
        </div>
      </Modal>
    </MobileLayout>
  );
};

export default PersonalRepository;
